import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Typography, Box } from '@mui/material';
import { CategoryTypeEnum } from 'entities/Files/types';
import { CategoriesSettingsType } from 'entities/Partners/types';
import { Select } from 'shared/ui/Select';
import { Loader } from 'shared/ui/Loader';
import { ColorPalette } from 'shared/consts/colorPalette';
import { ActionsForDialog } from 'shared/ui/ActionsForDialog';
import { Paper } from 'shared/ui/Paper';
import { useModelsOptions } from 'shared/hooks/use-models-options';

interface CategoriesModalPropsType {
	categoriesList: string[];
	open: boolean;
	handleClose: () => void;
	saveCategories: (data: CategoriesSettingsType) => void;
	isLoading?: boolean;
	isMultiple?: boolean;
}

export const CategoriesModal: FC<CategoriesModalPropsType> = ({
	categoriesList,
	open,
	handleClose,
	isLoading,
	saveCategories,
	isMultiple = true,
}) => {
	const { t } = useTranslation();
	const { modelsOptions, getModelType } = useModelsOptions();
	const [errorMessage, setErrorMessage] = useState(false);
	const [selectedOptions, setSelectedOptions] = useState<string | string[]>();

	const optionsForSelect = useMemo(() => {
		if (isMultiple) {
			return modelsOptions.filter((item) => !categoriesList.includes(item.value as string));
		}
		return modelsOptions.filter((item) => categoriesList.includes(item.value as string));
	}, [modelsOptions, categoriesList, isMultiple]);

	const submitCategoriesSettingsHandler = () => {
		if (selectedOptions) {
			const categoriesToMap = isMultiple
				? [...categoriesList, ...(selectedOptions as string[])]
				: [selectedOptions as string];
			const categories = categoriesToMap.map((option) => {
				const modelType = getModelType(option);
				if (modelType === CategoryTypeEnum.custom) {
					return { category: modelType as CategoryTypeEnum, customModelId: option };
				}
				if (modelType === CategoryTypeEnum.generative_model) {
					return { category: modelType as CategoryTypeEnum, generativeModelId: option };
				}
				return { category: option as CategoryTypeEnum };
			});
			saveCategories({ categories });
			setErrorMessage(false);
			setSelectedOptions(undefined);
		} else {
			setErrorMessage(true);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={() => {
				handleClose();
				setSelectedOptions(undefined);
				setErrorMessage(false);
			}}
			PaperComponent={Paper}
			PaperProps={{ title: t('partners.modals.select_model_title') as string }}
		>
			{isLoading ? (
				<Loader />
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
					<Typography variant={'body1'} color={ColorPalette.baliHai}>
						{t('partners.modals.select_model_description')}
					</Typography>

					<Select
						sx={{ margin: '0' }}
						label={t('partners.modals.select_model_title') as string}
						placeholder={t('partners.placeholder.model_type')}
						value={selectedOptions || []}
						onSelect={(value) => {
							if (isMultiple) {
								setSelectedOptions([...(value as string[])]);
							} else {
								setSelectedOptions(value as string);
							}
						}}
						options={optionsForSelect}
						errorText={errorMessage ? (t('partners.placeholder.model_type') as string) : ''}
						multiple={isMultiple}
						required
					/>

					<ActionsForDialog
						cancelLabel={t('shared_texts.cancel')}
						cancelAction={handleClose}
						applyLabel={t('shared_texts.save')}
						applyAction={submitCategoriesSettingsHandler}
						applyActionDisabled={false}
					/>
				</Box>
			)}
		</Dialog>
	);
};
